import React, { useState, useEffect, useCallback } from 'react';
import { workflowsApi } from '../services/api';
import { computeVisibleSteps } from '../utils/workflowTree';
import WorkflowProgressBar from './WorkflowProgressBar';

const GLYPH = { todo: '', in_progress: '', done: '✓', skipped: '–' };

/**
 * Panneau latéral d'accès rapide aux exécutions en cours de l'utilisateur.
 * Monté au niveau du shell Dashboard (comme ChatPanel) — n'altère pas la page courante.
 */
export default function WorkflowsPanel({ isOpen, onClose, onOpenExecution }) {
  const [running, setRunning] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [execution, setExecution] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadRunning = useCallback(async () => {
    try {
      const { data } = await workflowsApi.myRunning();
      setRunning(data.executions || []);
      setSelectedId((prev) => prev || (data.executions[0]?.id ?? null));
    } catch { /* silencieux */ }
  }, []);

  useEffect(() => { if (isOpen) loadRunning(); }, [isOpen, loadRunning]);

  const loadExec = useCallback(async (id) => {
    if (!id) { setExecution(null); return; }
    try { const { data } = await workflowsApi.getExecution(id); setExecution(data.execution); }
    catch { setExecution(null); }
  }, []);

  useEffect(() => { if (isOpen && selectedId) loadExec(selectedId); }, [isOpen, selectedId, loadExec]);

  const run = async (fn) => {
    setBusy(true);
    try { const { data } = await fn(); setExecution(data.execution); loadRunning(); }
    catch (e) { alert(e?.response?.data?.error || 'Action impossible.'); }
    finally { setBusy(false); }
  };

  if (!isOpen) return null;

  const visible = execution ? computeVisibleSteps(execution.snapshot_steps || [], execution.answers || {}) : [];
  const runtime = execution?.runtime_state || {};
  const answers = execution?.answers || {};

  return (
    <>
      <div className="wf-panel-backdrop" onClick={onClose} />
      <aside className="wf-side">
        <div className="wf-side-header">
          <h3>🔀 Mes workflows en cours</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="wf-side-body">
          {running.length === 0 ? (
            <div className="wf-side-empty">Aucune exécution en cours.</div>
          ) : (
            <>
              <select className="page-select wf-side-select" value={selectedId || ''} onChange={(e) => { setSelectedId(Number(e.target.value)); setExpanded(null); }}>
                {running.map((e) => <option key={e.id} value={e.id}>{e.name} — {e.progress_pct}%</option>)}
              </select>

              {execution && (
                <>
                  <WorkflowProgressBar pct={execution.progress_pct} color={execution.template_color} />

                  <div className="wf-side-steps">
                    {visible.map((v, idx) => {
                      const isFirst = idx === 0, isLast = idx === visible.length - 1;
                      const ml = v.depth ? { marginLeft: `${v.depth * 13}px` } : undefined;
                      if (v.type === 'decision') {
                        const chosen = answers[v.id];
                        return (
                          <div key={v.id} className={`wf-side-item ${isFirst ? 'is-first' : ''} ${isLast ? 'is-last' : ''}`} style={ml}>
                            <div className="wf-side-rail"><span className={`wf-side-badge decision ${chosen ? 'answered' : ''}`}>⑂</span></div>
                            <div>
                              <div className="wf-side-dec-q">{v.node.question || 'Décision'}</div>
                              <div className="wf-side-choices">
                                {(v.node.answers || []).map((a) => (
                                  <button key={a.id} className="wf-side-choice"
                                    style={chosen === a.id ? { background: a.color, borderColor: a.color, color: '#fff' } : { borderColor: a.color, color: a.color }}
                                    disabled={busy}
                                    onClick={() => run(() => workflowsApi.answerDecision(execution.id, v.id, chosen === a.id ? null : a.id))}>
                                    {a.label || '—'}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      const st = runtime[v.id]?.status || 'todo';
                      const isOpen2 = expanded === v.id;
                      const condBlocked = v.node.completion_condition && !runtime[v.id]?.condition_checked;
                      return (
                        <div key={v.id} className={`wf-side-item ${isFirst ? 'is-first' : ''} ${isLast ? 'is-last' : ''}`} style={ml}>
                          <div className="wf-side-rail"><span className={`wf-side-badge st-${st}`}>{GLYPH[st]}</span></div>
                          <div className={`wf-side-card ${st === 'in_progress' ? 'current' : ''} ${st === 'done' ? 'done' : ''} ${st === 'skipped' ? 'skipped' : ''}`}>
                            <span className="wf-side-title" onClick={() => setExpanded(isOpen2 ? null : v.id)}>{v.node.title || 'Étape'}</span>
                            {isOpen2 && (
                              <div className="wf-side-detail">
                                {v.node.description && <span>{v.node.description}</span>}
                                {v.node.completion_condition && <span>✔️ {v.node.completion_condition}</span>}
                                {v.node.link?.value && (v.node.link.kind === 'url'
                                  ? <a href={v.node.link.value} target="_blank" rel="noreferrer">🔗 lien</a>
                                  : <span>🔗 {v.node.link.value}</span>)}
                                {runtime[v.id]?.note && <span>📝 {runtime[v.id].note}</span>}
                              </div>
                            )}
                            <div className="wf-side-act">
                              {st === 'todo' && <button className="wf-mini primary" disabled={busy} onClick={() => run(() => workflowsApi.mutateStep(execution.id, v.id, 'start'))}>Démarrer</button>}
                              {st === 'in_progress' && <>
                                <button className="wf-mini primary" disabled={busy || condBlocked} title={condBlocked ? 'Condition requise (déplier)' : ''}
                                  onClick={() => run(() => workflowsApi.mutateStep(execution.id, v.id, 'complete'))}>Terminer</button>
                                <button className="wf-mini" disabled={busy} onClick={() => run(() => workflowsApi.mutateStep(execution.id, v.id, 'skip'))}>Passer</button>
                              </>}
                              {(st === 'done' || st === 'skipped') && <button className="wf-mini" disabled={busy} onClick={() => run(() => workflowsApi.mutateStep(execution.id, v.id, 'reopen'))}>Réouvrir</button>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="wf-side-foot">
                    <button className="wf-act" style={{ width: '100%' }} onClick={() => { onClose(); onOpenExecution(execution.id); }}>Voir en détail →</button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}
